import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { CreditCard, Download, Check, AlertCircle } from "lucide-react";

const SEPAMandate = () => {
  const { user } = useAuth();
  const [mandate, setMandate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    iban: '',
    bic: '',
    titulaire: '',
    adresse_titulaire: ''
  });

  useEffect(() => {
    fetchMandate();
  }, []);

  const fetchMandate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sepa/mandate`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMandate(data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching mandate:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user?.structure_id) {
      toast.error('Utilisateur non lié à une structure');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sepa/mandate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          structure_id: user.structure_id
        })
      });

      if (response.ok) {
        toast.success('Mandat SEPA créé avec succès');
        fetchMandate();
        resetForm();
      } else {
        throw new Error('Failed to create mandate');
      }
    } catch (error) {
      console.error('Error creating mandate:', error);
      toast.error('Erreur lors de la création du mandat');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sepa/mandate/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Decode base64 and create download
        const byteCharacters = atob(data.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'text/html' });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Mandat téléchargé');
      } else {
        throw new Error('Failed to download mandate');
      }
    } catch (error) {
      console.error('Error downloading mandate:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const resetForm = () => {
    setFormData({
      iban: '',
      bic: '',
      titulaire: '',
      adresse_titulaire: ''
    });
  };

  const formatIBAN = (value) => {
    return value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim().toUpperCase();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F5156] mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          Mandat de Prélèvement SEPA
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Configurez votre mandat de prélèvement pour les paiements automatiques
        </p>
      </div>

      {mandate ? (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Mandat SEPA Actif
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    mandate.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {mandate.status === 'active' ? <Check className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                    {mandate.status === 'active' ? 'Actif' : 'En attente'}
                  </span>
                </CardTitle>
                <CardDescription className="mt-2">
                  Référence Unique de Mandat (RUM): <span className="font-mono">{mandate.mandate_reference}</span>
                </CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={handleDownload}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label className="text-sm text-slate-600">Titulaire du compte</Label>
                <p className="font-medium">{mandate.titulaire}</p>
              </div>
              <div>
                <Label className="text-sm text-slate-600">Adresse</Label>
                <p className="font-medium">{mandate.adresse_titulaire}</p>
              </div>
              <div>
                <Label className="text-sm text-slate-600">IBAN</Label>
                <p className="font-mono font-medium">{formatIBAN(mandate.iban)}</p>
              </div>
              <div>
                <Label className="text-sm text-slate-600">BIC</Label>
                <p className="font-mono font-medium">{mandate.bic || 'N/A'}</p>
              </div>
            </div>

            {mandate.status === 'pending' && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-900">Mandat en attente de signature</p>
                    <p className="text-sm text-amber-800 mt-1">
                      Veuillez télécharger le mandat, le signer et le retourner pour activer les prélèvements automatiques.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 text-sm text-slate-600 space-y-2">
              <p>
                Ce mandat autorise Nexus SIRH à envoyer des instructions à votre banque pour débiter votre compte, 
                et votre banque à débiter votre compte conformément aux instructions de Nexus SIRH.
              </p>
              <p>
                Vous bénéficiez du droit d'être remboursé par votre banque selon les conditions décrites dans la 
                convention que vous avez passée avec elle. Une demande de remboursement doit être présentée dans 
                les 8 semaines suivant la date de débit de votre compte.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Créer un mandat SEPA</CardTitle>
            <CardDescription>
              Remplissez les informations bancaires pour configurer le prélèvement automatique
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="titulaire">Titulaire du compte *</Label>
                <Input
                  id="titulaire"
                  value={formData.titulaire}
                  onChange={(e) => setFormData({...formData, titulaire: e.target.value})}
                  placeholder="Nom complet du titulaire"
                  required
                />
              </div>

              <div>
                <Label htmlFor="adresse">Adresse complète du titulaire *</Label>
                <Input
                  id="adresse"
                  value={formData.adresse_titulaire}
                  onChange={(e) => setFormData({...formData, adresse_titulaire: e.target.value})}
                  placeholder="Adresse, Code postal, Ville"
                  required
                />
              </div>

              <div>
                <Label htmlFor="iban">IBAN *</Label>
                <Input
                  id="iban"
                  value={formData.iban}
                  onChange={(e) => setFormData({...formData, iban: e.target.value.replace(/\s/g, '')})}
                  placeholder="FR76 1234 5678 9012 3456 7890 123"
                  className="font-mono"
                  maxLength={34}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Format: FR suivi de 25 chiffres (espaces autorisés)
                </p>
              </div>

              <div>
                <Label htmlFor="bic">BIC / SWIFT (optionnel)</Label>
                <Input
                  id="bic"
                  value={formData.bic}
                  onChange={(e) => setFormData({...formData, bic: e.target.value.toUpperCase()})}
                  placeholder="BNPAFRPP"
                  className="font-mono"
                  maxLength={11}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Code identifiant de votre banque (8 ou 11 caractères)
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <p className="text-sm text-blue-900">
                  <strong>Note importante:</strong> En créant ce mandat, vous autorisez Nexus SIRH à effectuer 
                  des prélèvements sur votre compte bancaire selon les termes de votre abonnement. Vous pourrez 
                  télécharger le mandat signé après création.
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#0F5156] hover:bg-[#0a3a3e]"
                disabled={creating}
              >
                {creating ? 'Création...' : 'Créer le mandat SEPA'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SEPAMandate;
